import type { TrackSummary } from '@anytracker/core'
import { useNavigate } from 'react-router-dom'

import { IconButton } from '../../components/Button.js'
import { Sparkline } from '../../charts/Sparkline.js'
import { trackColour } from '../../charts/colours.js'
import { formatDate } from '../../format/dates.js'
import { kindLabel } from './kind-label.js'
import styles from './TrackList.module.css'

interface Props {
  readonly summary: TrackSummary
  readonly position: number
  readonly total: number
  readonly onMove: (from: number, to: number) => void
  readonly onArchive: () => void
  readonly onRestore: () => void
  readonly onRemove: () => void
  readonly dragging: boolean
  readonly dropTarget: boolean
  readonly onDragStart: () => void
  readonly onDragEnter: () => void
  readonly onDragEnd: () => void
}

export function TrackRow({
  summary,
  position,
  total,
  onMove,
  onArchive,
  onRestore,
  onRemove,
  dragging,
  dropTarget,
  onDragStart,
  onDragEnter,
  onDragEnd,
}: Props) {
  const navigate = useNavigate()
  const { track, measurementCount, firstAt, lastAt, recent } = summary

  const classes = [
    styles['row'],
    dragging ? styles['dragging'] : '',
    dropTarget ? styles['dropTarget'] : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={classes}
      draggable={!track.archived}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragOver={(event) => event.preventDefault()}
      onDragEnd={onDragEnd}
      onDrop={(event) => {
        event.preventDefault()
        onDragEnd()
      }}
    >
      {track.archived ? (
        <span />
      ) : (
        /*
         * Two ways to reorder, and the buttons are the primary one.
         *
         * HTML5 drag-and-drop does not work on touch at all, and this is a
         * phone-first app — so dragging is the desktop convenience and these
         * are what actually ship the feature. They are also keyboard-operable
         * for free, which a custom pointer-event drag would not be.
         */
        <span className={styles['actions']}>
          <IconButton
            label={`Move ${track.name} up`}
            disabled={position === 0}
            onClick={() => onMove(position, position - 1)}
          >
            <Chevron direction="up" />
          </IconButton>
          <IconButton
            label={`Move ${track.name} down`}
            disabled={position === total - 1}
            onClick={() => onMove(position, position + 1)}
          >
            <Chevron direction="down" />
          </IconButton>
        </span>
      )}

      <button
        type="button"
        className={styles['body']}
        onClick={() => void navigate(`/tracks/${track.id}`)}
      >
        <span className={styles['nameRow']}>
          <span
            className={styles['swatch']}
            style={{ background: trackColour(track.id) }}
            aria-hidden="true"
          />
          <span className={styles['name']}>
            {track.name}
            {track.unit !== undefined && (
              <span className={styles['unit']}>{track.unit}</span>
            )}
          </span>
          {/* Nothing at all for a track never recorded — see Sparkline. */}
          <Sparkline values={recent} colour={trackColour(track.id)} />
        </span>
        <span className={styles['meta']}>
          <span className={styles['kind']}>{kindLabel(track)}</span>
          {measurementCount === 0 ? (
            <span>Never recorded</span>
          ) : (
            <>
              <span>
                {measurementCount === 1
                  ? '1 measurement'
                  : `${String(measurementCount)} measurements`}
              </span>
              {firstAt !== undefined && lastAt !== undefined && (
                <span>
                  {formatDate(firstAt)} – {formatDate(lastAt)}
                </span>
              )}
            </>
          )}
        </span>
      </button>

      <span className={styles['actions']}>
        {track.archived ? (
          <IconButton label={`Restore ${track.name}`} onClick={onRestore}>
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <path
                d="M4 10a6 6 0 1 1 1.8 4.3M4 6v4h4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </IconButton>
        ) : (
          <IconButton label={`Archive ${track.name}`} onClick={onArchive}>
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <path
                d="M3 6h14M4 6v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6M8 10h4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </IconButton>
        )}
        <IconButton label={`Remove ${track.name}`} onClick={onRemove}>
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path
              d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
      </span>
    </div>
  )
}

function Chevron({ direction }: { readonly direction: 'up' | 'down' }) {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d={direction === 'up' ? 'M5 12l5-5 5 5' : 'M5 8l5 5 5-5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
